import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Group, UserRole } from '../apis/api-client';
import { groupsClient } from '../apis/client-instances';
import { RootState } from '../types';

interface OwnedGroupsContextValue {
  /** Groups the current learner owns (the "group admin" of). */
  ownedGroups: Group[];
  loading: boolean;
  /** True once we know the learner owns at least one group. */
  isGroupAdmin: boolean;
}

const OwnedGroupsContext = createContext<OwnedGroupsContextValue>({
  ownedGroups: [],
  loading: false,
  isGroupAdmin: false,
});

/**
 * Resolves the current learner's owned groups ONCE per session and shares
 * them via context, so the several consumers (MainLayout sidebar gate, the
 * NavBar entry, and the MyGroups page) don't each re-hit `/groups/owned`.
 *
 * Only learners can be group admins in the product sense, so we fetch only
 * for `UserRole.User`; admins manage groups through the admin surfaces and
 * would get an empty list anyway. Refetches when the signed-in user changes.
 * Ownership is resolved live server-side, so a reassigned owner is reflected
 * on the next load without a re-login.
 */
export function OwnedGroupsProvider({ children }: { children: ReactNode }) {
  const userId = useSelector((state: RootState) => state.profile.user?.id);
  const role = useSelector((state: RootState) => state.profile.user?.role);
  const isLearner = role === UserRole.User;

  const [ownedGroups, setOwnedGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLearner || userId == null) {
      setOwnedGroups([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    groupsClient
      .getOwnedGroups()
      .then((groups) => {
        if (!cancelled) setOwnedGroups(groups ?? []);
      })
      .catch(() => {
        if (!cancelled) setOwnedGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLearner, userId]);

  const value = useMemo<OwnedGroupsContextValue>(
    () => ({ ownedGroups, loading, isGroupAdmin: ownedGroups.length > 0 }),
    [ownedGroups, loading],
  );

  return <OwnedGroupsContext.Provider value={value}>{children}</OwnedGroupsContext.Provider>;
}

export function useOwnedGroups(): OwnedGroupsContextValue {
  return useContext(OwnedGroupsContext);
}
